import { useState } from "react";
import { Bell } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNotifications } from "@/hooks/useNotifications";

const TONE_CLASSES = {
  emerald: "bg-emerald-50 text-emerald-700",
  amber: "bg-amber-50 text-amber-800",
  rose: "bg-rose-50 text-rose-700",
  blue: "bg-blue-50 text-blue-700",
  purple: "bg-purple-50 text-purple-700",
  indigo: "bg-indigo-50 text-indigo-700",
  cyan: "bg-cyan-50 text-cyan-700",
  slate: "bg-slate-100 text-slate-700",
};

export function NotificationsBell() {
  const { events, connected, unseenCount, markAllSeen, clear } = useNotifications();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const togglePanel = () => {
    if (!open) markAllSeen();
    setOpen(!open);
  };

  return (
    <div className="relative" data-testid="notifications-bell-wrapper">
      <button
        type="button"
        onClick={togglePanel}
        className="relative flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-700"
        aria-label="Notifiche"
        data-testid="notifications-bell-button"
      >
        <Bell className="h-4 w-4" />
        <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white ${connected ? "bg-emerald-500" : "bg-slate-300"}`} data-testid="notifications-status-indicator" />
        {unseenCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white" data-testid="notifications-unseen-count">
            {unseenCount > 9 ? "9+" : unseenCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 z-50 w-80 rounded-lg border border-slate-200 bg-white shadow-xl" data-testid="notifications-panel">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
            <p className="text-sm font-semibold text-slate-800">{t("notifications_ui.title")}</p>
            <button onClick={clear} className="text-xs text-slate-500 hover:text-slate-700" data-testid="notifications-clear-button">{t("notifications_ui.clear")}</button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {events.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-slate-500" data-testid="notifications-empty">{t("notifications_ui.empty")}</p>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="notifications-list">
                {events.map((e) => (
                  <li key={e.id} className="flex items-start gap-3 px-4 py-2.5" data-testid={`notification-item-${e.id}`}>
                    <span className={`mt-0.5 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${TONE_CLASSES[e.tone] || TONE_CLASSES.slate}`}>{e.label}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs text-slate-700">{e.entity_type} · {e.user_name}</p>
                      <p className="text-[10px] text-slate-400">{e.created_at.slice(11, 19)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t border-slate-100 px-4 py-2 text-[10px] text-slate-400">
            WebSocket: {connected ? t("notifications_ui.connected") : t("notifications_ui.offline")} · /audit-log
          </div>
        </div>
      )}
    </div>
  );
}
